import { z } from 'zod';
/**
 * Uren schrijven via de kennisbank → TimeChimp (Onderdeel 5).
 *
 * TimeChimp is de bron van waarheid; de kennisbank spiegelt niets permanent.
 * Zie docs/plans/onderdeel-5-timechimp.md en docs/research/timechimp-api-v2.md.
 */
/** TimeChimp interne goedkeurstatus (subset die de UI kent; API kent er meer). */
export const TimeChimpStatus = z.enum([
    'Open',
    'PendingApproval',
    'Approved',
    'Invoiced',
    'WrittenOff',
    'Rejected',
]);
const DateYmd = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet als JJJJ-MM-DD worden aangeleverd.')
    .refine((s) => !Number.isNaN(Date.parse(s)), { message: 'Ongeldige datum.' });
/** Body voor POST /api/v1/uren en PUT /api/v1/uren/:id. */
export const UrenEntryInput = z.object({
    date: DateYmd,
    projectId: z.number().int().positive(),
    taskId: z.number().int().positive(),
    /** Uren in decimale notatie, kwartierstappen (TimeChimp-conventie). */
    hours: z
        .number()
        .positive()
        .max(24)
        .refine((h) => Number.isInteger(h * 4), {
        message: 'Uren moeten in kwartierstappen worden ingevoerd (0,25).',
    }),
    notes: z.string().max(500).optional(),
});
/**
 * Body voor POST /api/v1/uren/week: één project/activiteit met uren per dag.
 * Levert één TimeChimp-regel per dag op, net als losse invoer.
 */
export const UrenWeekInput = UrenEntryInput.omit({ date: true, hours: true }).extend({
    days: z
        .array(UrenEntryInput.pick({ date: true, hours: true }))
        .min(1, 'Vul voor minstens één dag uren in.')
        .max(7),
});
/** Query voor GET /api/v1/uren (en beheer-rapportage): een gesloten datumbereik. */
export const UrenRangeQuery = z.object({
    from: DateYmd,
    to: DateYmd,
});
/** Body voor POST /api/v1/uren/aanbieden: alle eigen Open-regels in het bereik. */
export const UrenSubmitInput = UrenRangeQuery;
/**
 * Koppelstatus per medewerker (besluit Jos 3 sep 2026).
 *
 * `automatisch` en `handmatig` zijn allebei een vastgelegd TimeChimp-user-id;
 * het verschil is alleen wie het zette (de e-mailmatch of een beheerder), en
 * dat staat in de tabel zodat een verkeerde koppeling naar een persoon te
 * herleiden is. `verlopen` = het vastgelegde id bestaat niet meer (of is
 * inactief) in TimeChimp: die medewerker kan niet schrijven tot een beheerder
 * het rechtzet, want stil terugvallen op de e-mailmatch kan uren op de
 * verkeerde persoon boeken. `ongekoppeld` = nog nooit gematcht.
 */
export const TimeChimpKoppelStatus = z.enum([
    'automatisch',
    'handmatig',
    'verlopen',
    'geen',
    'ongekoppeld',
]);
/**
 * Body voor PUT /api/v1/beheer/timechimp/koppeling/:userId.
 * `onbepaald` wist de koppeling: de e-mailmatch mag het weer proberen.
 */
export const TimeChimpKoppelingInput = z
    .object({
    state: z.enum(['gekoppeld', 'geen', 'onbepaald']),
    timechimpUserId: z.number().int().positive().optional(),
})
    .refine((v) => v.state !== 'gekoppeld' || v.timechimpUserId !== undefined, {
    message: 'Kies een TimeChimp-account om aan te koppelen.',
    path: ['timechimpUserId'],
});
/** Body voor POST /api/v1/beheer/timechimp/toewijzingen/project. */
export const TimeChimpProjectRefreshInput = z.object({
    projectId: z.number().int().positive(),
});
//# sourceMappingURL=timechimp.js.map